// components/billing/confirm-plan-change-modal.tsx
"use client";

import React from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Divider } from "@heroui/react"; // Use HeroUI components
import { Doc } from "@/convex/_generated/dataModel";
import { ArrowRight } from "lucide-react";
interface ConfirmPlanChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlanName?: string | null;
  newPlan: Doc<"plans"> | null;
  billingInterval: "monthly" | "yearly";
  onConfirm: (planToSwitchTo: Doc<"plans">) => Promise<void>; // Pass the plan to confirm
  isLoading: boolean;
}
export default function ConfirmPlanChangeModal({
  isOpen,
  onClose,
  currentPlanName,
  newPlan,
  billingInterval,
  onConfirm,
  isLoading
}: ConfirmPlanChangeModalProps) {
  if (!newPlan) return null; // Don't render if no plan is selected for change

  const newPrice = billingInterval === "yearly" ? newPlan.targetPrice : newPlan.initialPrice ?? newPlan.targetPrice;
  const intervalText = billingInterval === "yearly" ? "/year" : "/month";
  const currencySymbol = newPlan.currency === "usd" ? "$" : newPlan.currency;
  const handleConfirm = async () => {
    await onConfirm(newPlan); // Call the confirmation handler passed from parent
    // Keep modal open during loading, parent will close on success/error handling
  };
  return <Modal isOpen={isOpen} onOpenChange={onClose} backdrop="blur" data-sentry-element="Modal" data-sentry-component="ConfirmPlanChangeModal" data-sentry-source-file="confirm-plan-change-modal.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="confirm-plan-change-modal.tsx">
        {(onCloseModal // Use onCloseModal provided by HeroUI
      ) => <>
            <ModalHeader className="flex flex-col gap-1 text-lg md:text-xl">
              Confirm Plan Change
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4 text-sm md:text-base">
                {currentPlanName && <p>
                    You are currently on the{" "}
                    <span className="font-semibold">
                      {currentPlanName.replace(/ Monthly| Yearly/, "")}
                    </span>{" "}
                    plan.
                  </p>}
                {!currentPlanName && <p>You are about to subscribe to the:</p>}
                <div className="flex items-center justify-center gap-2 rounded-lg border bg-primary/5 p-4 dark:bg-primary/10">
                  <span className="font-semibold">
                    {newPlan.name.replace(/ Monthly| Yearly/, "")}
                  </span>
                  <ArrowRight size={16} className="text-muted-foreground" />
                  <span className="font-bold text-primary">
                    {currencySymbol}
                    {newPrice}
                    {intervalText}
                  </span>
                </div>

                <p className="text-xs text-muted-foreground">
                  By confirming, your subscription will be updated immediately.
                  Stripe will automatically calculate any prorated charges or
                  credits, which will be reflected on your next invoice. Your
                  next billing date will adjust based on the change.
                </p>
                {/* Add more details here if needed */}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onCloseModal} disabled={isLoading}>
                Cancel
              </Button>
              <Button color="primary" onPress={handleConfirm} isLoading={isLoading} disabled={isLoading}>
                {isLoading ? "Processing..." : `Confirm Switch to ${newPlan.name.replace(/ Monthly| Yearly/, "")}`}
              </Button>
            </ModalFooter>
          </>}
      </ModalContent>
    </Modal>;
}